import { useEffect, useState } from "react";
import { X, Loader2, UploadCloud, CheckCircle2 } from "lucide-react";
import api, { API } from "@/lib/api";
import { toast } from "sonner";

const PHONE_RE = /^[6-9]\d{9}$/;
const EMAIL_RE = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;

export default function PartnerModal({ open, onClose }) {
  const [form, setForm] = useState({ name: "", phone: "", email: "", course_details: "", consent: false });
  const [pdf, setPdf] = useState({ url: "", name: "" });
  const [errors, setErrors] = useState({});
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!open) {
      setForm({ name: "", phone: "", email: "", course_details: "", consent: false });
      setPdf({ url: "", name: "" });
      setErrors({}); setUploading(false); setLoading(false); setDone(false);
    }
  }, [open]);

  if (!open) return null;

  const change = (k) => (e) => {
    const v = k === "consent" ? e.target.checked : e.target.value;
    setForm({ ...form, [k]: v });
    if (errors[k]) setErrors({ ...errors, [k]: null });
  };

  const uploadPdf = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error("File too large (max 10MB)"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload/public", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPdf({ url: `${API}/files/${data.id}`, name: file.name });
      toast.success("File uploaded");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); }
  };

  const validate = () => {
    const next = {};
    if (!form.name.trim()) next.name = "Please enter your name";
    if (!PHONE_RE.test(form.phone.trim())) next.phone = "Enter a valid 10-digit mobile number";
    if (!EMAIL_RE.test(form.email.trim())) next.email = "Enter a valid email address";
    if (!form.consent) next.consent = "Please provide consent to continue";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);
    try {
      await api.post("/partner-leads", {
        name: form.name.trim(),
        phone: form.phone.trim(),
        email: form.email.trim(),
        course_details: form.course_details.trim(),
        syllabus_pdf: pdf.url,
        consent: form.consent,
      });
      setDone(true);
      toast.success("Application submitted!");
      setTimeout(onClose, 2200);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally { setLoading(false); }
  };

  const inputCls = (err) =>
    `w-full px-4 py-3 rounded-xl border ${err ? "border-red-400" : "border-neutral-200"} bg-white text-sm focus:outline-none focus:ring-2 focus:ring-sky-500/40 focus:border-sky-500`;

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose} data-testid="partner-modal" role="dialog" aria-modal="true" aria-label="Become a partner">
      <div className="bg-white rounded-3xl max-w-md w-full max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="p-6 border-b border-neutral-100 flex items-start justify-between gap-4">
          <div>
            <div className="text-xs uppercase tracking-widest text-sky-500 font-semibold">Partner With Us</div>
            <h3 className="font-display font-bold text-xl mt-1">Become a Partner</h3>
            <p className="text-xs text-neutral-500 mt-1">Tell us about you and your course. We'll get back within 2 business days.</p>
          </div>
          <button onClick={onClose} aria-label="Close" className="p-1.5 hover:bg-neutral-100 rounded-full shrink-0 focus:outline-none focus:ring-2 focus:ring-sky-500" data-testid="partner-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        {!done ? (
          <form onSubmit={submit} noValidate className="p-6 space-y-3" data-testid="partner-form">
            <div>
              <label htmlFor="p-name" className="sr-only">Full name</label>
              <input id="p-name" placeholder="Full name*" value={form.name} onChange={change("name")} className={inputCls(errors.name)} data-testid="partner-name" />
              {errors.name && <div className="text-xs text-red-500 mt-1">{errors.name}</div>}
            </div>
            <div>
              <label htmlFor="p-phone" className="sr-only">Contact number</label>
              <input id="p-phone" type="tel" inputMode="numeric" maxLength={10} placeholder="Contact number (10-digit)*" value={form.phone} onChange={change("phone")} className={inputCls(errors.phone)} data-testid="partner-phone" />
              {errors.phone && <div className="text-xs text-red-500 mt-1">{errors.phone}</div>}
            </div>
            <div>
              <label htmlFor="p-email" className="sr-only">Email</label>
              <input id="p-email" type="email" placeholder="Email address*" value={form.email} onChange={change("email")} className={inputCls(errors.email)} data-testid="partner-email" />
              {errors.email && <div className="text-xs text-red-500 mt-1">{errors.email}</div>}
            </div>
            <div>
              <label htmlFor="p-course" className="sr-only">Course details</label>
              <textarea id="p-course" rows={3} placeholder="Course details — what do you want to teach / sell?" value={form.course_details} onChange={change("course_details")} className={inputCls(false)} data-testid="partner-course-details" />
            </div>
            <div>
              <label className="flex items-center justify-between gap-3 px-4 py-3 rounded-xl border border-dashed border-neutral-300 bg-neutral-50 cursor-pointer hover:border-sky-500 focus-within:ring-2 focus-within:ring-sky-500/40" data-testid="partner-pdf-label">
                <span className="text-sm text-neutral-600 flex items-center gap-2 truncate">
                  {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <UploadCloud className="w-4 h-4" />}
                  {pdf.name ? <span className="truncate">{pdf.name}</span> : "Upload syllabus PDF (optional)"}
                </span>
                <input type="file" accept="application/pdf,image/*" hidden onChange={uploadPdf} data-testid="partner-pdf-input" />
                <span className="text-xs font-semibold text-sky-600 shrink-0">Browse</span>
              </label>
            </div>
            <label className="flex items-start gap-2 text-xs text-neutral-600">
              <input type="checkbox" checked={form.consent} onChange={change("consent")} className="mt-0.5" data-testid="partner-consent" />
              <span>I agree to be contacted by EQtech Academy regarding this partnership and consent to my details being stored.*</span>
            </label>
            {errors.consent && <div className="text-xs text-red-500">{errors.consent}</div>}
            <button type="submit" disabled={loading || uploading} className="btn-primary-eq w-full justify-center" data-testid="partner-submit">
              {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Submitting…</> : "Submit Application"}
            </button>
          </form>
        ) : (
          <div className="p-8 text-center" data-testid="partner-done">
            <div className="w-12 h-12 mx-auto rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center">
              <CheckCircle2 className="w-6 h-6" />
            </div>
            <div className="font-display font-bold text-lg mt-3">Application received!</div>
            <p className="text-sm text-neutral-500 mt-1">Thanks for your interest. Our partnerships team will reach out soon.</p>
          </div>
        )}
      </div>
    </div>
  );
}
